import type { RoiCalculationResult } from "@marketing-sites/hooks/use-roi-calculator";
import { formatCurrency } from "@marketing-sites/utils/format-currency";
import { formatNumberWithCommas } from "@marketing-sites/utils/format-number";
import { cn } from "@marketing-sites/utils/cn";

interface RoiCalculatorResultsProps {
  result: RoiCalculationResult;
  accentTextClassName: string;
}

const COLUMNS = ["Before Fin", "Year 1", "Year 2", "Year 3", "Total"];

const formatCount = (value: number) => formatNumberWithCommas(value) || "0";

export function RoiCalculatorResults({
  result,
  accentTextClassName,
}: RoiCalculatorResultsProps) {
  const rows: { label: string; cells: string[]; emphasis?: boolean }[] = [
    {
      label: "Employee cost",
      cells: [
        formatCurrency(result.year0EmployeeCostPreFin),
        formatCurrency(result.year1EmployeeCost),
        formatCurrency(result.year2EmployeeCost),
        formatCurrency(result.year3EmployeeCost),
        formatCurrency(result.totalEmployeeCost),
      ],
    },
    {
      label: "Fin cost",
      cells: [
        "-",
        formatCurrency(result.year1FinCost),
        formatCurrency(result.year2FinCost),
        formatCurrency(result.year3FinCost),
        formatCurrency(result.totalFinCost),
      ],
    },
    {
      label: "Agents freed",
      cells: [
        "-",
        formatCount(result.year1AgentsFreed),
        formatCount(result.year2AgentsFreed),
        formatCount(result.year3AgentsFreed),
        formatCount(result.totalAgentsFreed),
      ],
    },
    {
      label: "Your savings",
      emphasis: true,
      cells: [
        "-",
        formatCurrency(result.year1Savings),
        formatCurrency(result.year2Savings),
        formatCurrency(result.year3Savings),
        formatCurrency(result.totalSavings),
      ],
    },
  ];

  return (
    <div className="flex min-w-0 flex-col gap-8" aria-live="polite">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-2">
          <h3 className="text-label-lg">Fin will save you</h3>
          <p
            className="text-heading-4 2xl:text-heading-3 whitespace-nowrap tabular-nums"
            data-testid="roi-total-savings"
          >
            <span className={accentTextClassName}>
              {formatCurrency(result.totalSavings)}
            </span>
          </p>
          <p className="text-body text-current/60">Over 3 years</p>
        </div>
        <div className="flex flex-col gap-2">
          <h3 className="text-label-lg">Return on investment</h3>
          <p
            className="text-heading-4 2xl:text-heading-3 whitespace-nowrap tabular-nums"
            data-testid="roi-return"
          >
            {formatCount(Math.round(Math.max(0, result.returnOnInvestment)))}%
          </p>
        </div>
      </div>

      <div className="border-current/10 overflow-x-auto border-t border-dashed pt-4">
        <table className="text-body w-full min-w-[560px] border-collapse text-left">
          <thead>
            <tr className="border-current/10 border-b">
              <th scope="col" className="py-3 pr-4">
                <span className="sr-only">Metric</span>
              </th>
              {COLUMNS.map((column) => (
                <th
                  key={column}
                  scope="col"
                  className="text-label-lg text-current/60 px-3 py-3 font-normal"
                >
                  {column}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-current/10 divide-y">
            {rows.map((row) => (
              <tr key={row.label}>
                <th
                  scope="row"
                  className="whitespace-nowrap py-3 pr-4 font-normal"
                >
                  {row.label}
                </th>
                {row.cells.map((cell, index) => (
                  <td
                    key={COLUMNS[index]}
                    className={cn(
                      "px-3 py-3 tabular-nums",
                      row.emphasis || index === row.cells.length - 1
                        ? "text-current"
                        : "text-current/60",
                    )}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
